<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader"
      variant="dark"
      opacity="0.85"
      blur="2px"
      spinner-variant="primary"
      spinner-small
      rounded="sm"
    >
      <alert-handler :alert-errors="alertErrors" />
      <b-card
        aria-hidden="true"
        class="mb-0"
        title="Contact Information"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>
              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="userData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_EMAIL')"
                  label-for="email"
                >
                  <b-form-input
                    id="email"
                    v-model="userData.email"
                    type="email"
                    trim
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PHONE')"
                  label-for="phone"
                >
                  <b-form-input
                    id="phone"
                    v-model="userData.phone"
                    trim
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Description -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_DESCRIPTION')"
                  label-for="Description"
                >
                  <b-form-textarea
                    id="password"
                    v-model="userData.description"
                    rows="3"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <hr>
            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>
              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  :rules="userData.address.road_name || userData.address.zip_code || userData.address.city_id || userData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="House Number"
                      v-model="userData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  :rules="userData.address.house_number || userData.address.zip_code || userData.address.city_id || userData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="userData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="userData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  :rules="userData.address.road_name || userData.address.house_number || userData.address.city_id || userData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <v-select
                      v-model="userData.address.zip_code"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="zipCodes"
                      :reduce="val => val.value"
                      :clearable="false"
                      @input="getCityId($event)"
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  :rules="userData.address.road_name || userData.address.house_number || userData.address.zip_code || userData.address.country_id ? 'required' : ''"
                >
                  <b-form-group
                    id="city_id"
                    :label="$t('DEBTIA_CITY')"
                    class="city"
                    label-for="city_id"
                  >
                    <v-select
                      v-model="userData.address.city_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="cities"
                      :reduce="val => val.value"
                      :clearable="false"
                      @input="getZipCode($event)"
                    />
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Country"
                  :rules="userData.address.road_name || userData.address.house_number || userData.address.zip_code || userData.address.city_id ? 'required' : ''"
                >
                  <b-form-group
                    id="country_id"
                    :label="$t('DEBTIA_COUNTRY')"
                    class="country"
                    label-for="country_id"
                  >
                    <v-select
                      v-model="userData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="false"
                      :value="'Select Country'"
                    >
                      <template
                        #selected-option-container="{ option }"
                      >
                        <div class="vs__selected">
                          {{ $t(option.label) }}</div>
                      </template>
                      <template
                        slot="option"
                        slot-scope="option"
                      >
                        {{ $t(option.label) }}
                      </template>
                    </v-select>
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
            <hr>
            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BCard,
  BFormInvalidFeedback,
  BOverlay,
  BFormTextarea,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import formValidation from '@core/comp-functions/forms/form-validation'
import { ref, reactive } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required, alphaNum, email } from '@validations'
import router from '@/router'
import $this from 'vue'
import countries from '@/countries'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import contactStoreModule from '../contactStoreModule'
import AlertHandler from './AlertHandler.vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    AlertHandler,
    BOverlay,
    BFormTextarea,
  },
  data() {
    return {
      busy: false,
      countries,
      required,
      alphaNum,
      email,
      termSheetCodeLoader: false,
      selectedTermSheetCode: '',
      termSheetCodeOptions: [],
      termSheetCode: '',
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
      },
      maxCharacters: 50,
      maxCharactersAllowed: 50,
      zipCodes: [],
      cities: [],
    }
  },
  setup() {
    const blankUserData = {
      name: '',
      email: null,
      phone: null,
      description: null,
      address: {
        road_name: null,
        house_number: null,
        place: null,
        zip_code: null,
        city_id: null,
        country_id: null,
      },
    }
    const userData = reactive(blankUserData)
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }
    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)
    const alertErrors = ref([])
    const loader = ref(false)
    const onSubmit = async () => {
      loader.value = true
      const data = { ...userData }
      // this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-contact'
      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, contactStoreModule)
      store.dispatch('app-contact/addContact', data)
        .then(() => {
          loader.value = true
          router.replace('/contacts/list')
            .then(() => {
              $this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Contact Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Contact successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          alertErrors.value = []
          alertErrors.value.push(...error.data.message)
          loader.value = false
        })
      return true
    }

    return {
      refFormObserver,
      getValidationState,
      resetForm,
      userData,
      onSubmit,
      alertErrors,
      loader,
    }
  },
  watch: {
    selectedTermSheetCode: {
      handler() {
        this.userData.termSheetCode = this.selectedTermSheetCode
      },
    },
  },
  created() {
    this.getZipCodesDropdown()
    this.getCitiesDropdown()
  },
  methods: {
    async getZipCodesDropdown() {
      await axios.get('settings/zip-codes/dropdown?country=Danmark')
        .then(response => {
          this.zipCodes = response.data.data
        }).catch(() => false)
    },
    async getCitiesDropdown() {
      await axios.get('settings/get-cities-dropdown?country=Danmark')
        .then(response => {
          this.cities = response.data.data
        }).catch(error => {
          console.log(error)
        })
    },
    async getCityId(event) {
      await axios.get(`settings/get-city-id-by-zip-code?code=${event}`)
        .then(response => {
          if (response.data.data.city_name !== null) {
            this.userData.address.city_id = response.data.data.city_id
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async getZipCode(event) {
      await axios.get(`settings/get-zip-code-by-city-id?city_id=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.userData.address.zip_code = response.data.data.zip_code
          }
        }).catch(error => {
          console.log(error)
        })
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
  },
}
</script>
